import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Card, Checkbox, Col, DatePicker, Input, Modal, Radio, Row, Select, Tooltip } from 'antd';
import moment from 'moment';
import { describeOpsError, getOpsTicket, listOpsTickets, readOpsToken } from '../../util/opsApi';
import OpsTicketDetail, { EMPTY, formatDate, StatusTag } from '../Asset/OpsTicketDetail';
import { ExportableTable, strSorter } from '../Fuel/report/tableKit';

/**
 * IoT Tickets Report — every Ops (iot-ops) support ticket of the organisation,
 * open and closed, with filters and CSV export. Read-only. See docs/ops-iot-tickets.md.
 *
 * API calls:
 *  - page open / Refresh: GET /setting/organisation/:id (AssetPro) for the Ops
 *    token, then GET {Ops}/integration/support-tickets?state=open
 *  - first time the user picks All or Closed: GET ...?state=closed (then reused;
 *    Refresh reloads it only if it was already loaded)
 *  - GET {Ops}/integration/support-tickets/:id when a ticket is opened (cached per ticket)
 * All filtering happens client-side on that one list.
 *
 * Definitions:
 *  - Open   = Ops status category is not CLOSED (Closed and Cancelled are CLOSED).
 *  - Escalated (tile) = open tickets currently escalated.
 *  - Overdue = open, has a target date, and the target date is before today.
 *    A ticket with no target date is never counted as overdue.
 *  - A ticket without a status category is "Unknown" — never guessed as open or closed.
 */

const { RangePicker } = DatePicker;
const { Option } = Select;

const SOURCE_LABELS = { ASSETPRO: 'Client (AssetPro)', OPS: 'AssetPro team (Ops)' };

const stateOf = (t) => {
  const category = t.status && t.status.category;
  if (!category) return 'UNKNOWN';
  return category === 'CLOSED' ? 'CLOSED' : 'OPEN';
};

const isOverdue = (t) => stateOf(t) === 'OPEN' && !!t.targetDate
  && moment(t.targetDate).isBefore(moment().startOf('day'));

const csvDate = (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : '');
const csvDay = (v) => (v ? moment(v).format('YYYY-MM-DD') : '');

const CSV_HEADER = [
  'Ticket No', 'Status', 'Status Category', 'Open/Closed', 'Status Remark', 'Overdue', 'Escalated', 'Escalation Reason',
  'Asset Code', 'Owner Code', 'Asset Type', 'Asset Sub Type', 'Location', 'Sub Location',
  'Details of Complaint', 'Cause', 'Complaint Type', 'Raised By', 'Assigned To',
  'AssetPro Job Card', 'AssetPro Complaint', 'Complaint Date', 'Target Date', 'Next Review',
  'Last Status Update', 'Last Updated', 'Closed At', 'Cancelled At', 'Cancel Reason', 'Ops Ticket Id',
];

const csvRow = (t) => [
  t.ticketNo, t.status && t.status.name, t.status && t.status.category,
  { OPEN: 'Open', CLOSED: 'Closed', UNKNOWN: 'Unknown' }[stateOf(t)], t.statusRemark,
  isOverdue(t) ? 'Yes' : 'No', t.isEscalated ? 'Yes' : 'No', t.escalationReason,
  t.assetCode, t.ownerCode, t.assetType, t.assetSubType, t.location, t.subLocation,
  t.detailsOfComplaint, t.causeOfDamage,
  t.complaintType ? [t.complaintType.code, t.complaintType.issue].filter(v => v !== null && v !== undefined).join(' - ') : '',
  t.source ? SOURCE_LABELS[t.source] || t.source : '', t.assignedTo && t.assignedTo.name,
  t.assetproJobCardNo, t.assetproComplaintNo, csvDate(t.complaintDate), csvDay(t.targetDate), csvDay(t.nextReviewDate),
  csvDate(t.lastStatusUpdateAt), csvDate(t.updatedAt), csvDate(t.closedAt), csvDate(t.cancelledAt), t.cancelReason, t.id,
];

const distinct = (list, pick) => [...new Set(list.map(pick).filter(v => v !== null && v !== undefined && v !== ''))]
  .sort((a, b) => String(a).localeCompare(String(b)));

function SummaryTile({ label, value, color, active, onClick, hint }) {
  return (
    <Tooltip title={hint}>
      <div
        onClick={onClick}
        style={{
          flex: '1 1 140px', padding: '10px 14px', borderRadius: 8, cursor: onClick ? 'pointer' : 'default',
          border: `1px solid ${active ? color : '#f0f0f0'}`, background: active ? `${color}14` : '#fff',
        }}
      >
        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
      </div>
    </Tooltip>
  );
}

function OpsTicketsReport({ authUser }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openTickets, setOpenTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState(null); // null = not loaded yet
  const [closedLoading, setClosedLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [token, setToken] = useState(null);
  const [details, setDetails] = useState({}); // ticketId -> { loading, data, error }
  const [openTicketId, setOpenTicketId] = useState(null);

  // Filters
  const [state, setState] = useState('OPEN'); // OPEN | ALL | CLOSED | UNKNOWN
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [source, setSource] = useState(undefined);
  const [assignees, setAssignees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const orgId = authUser && authUser.organisation && authUser.organisation._id;

  // Fetches one side of the list and checks Ops really applied the filter: a
  // ticket in the wrong category means Ops ignored `state` (e.g. an older Ops),
  // and showing it would give wrong open/closed numbers.
  const fetchSide = async (tokenValue, side) => {
    const response = await listOpsTickets(tokenValue, side);
    if (!Array.isArray(response.data)) throw new Error('Ops returned an unexpected response for the ticket list.');
    const wrong = response.data.filter(t => (side === 'open' ? stateOf(t) === 'CLOSED' : stateOf(t) !== 'CLOSED'));
    if (wrong.length) {
      throw new Error(`Ops returned ${wrong.length} ticket(s) outside the requested "${side}" filter — the Ops module needs the ticket state filter. Nothing is shown to avoid wrong counts.`);
    }
    return response.data;
  };

  const errorText = (e) => (e && e.response ? describeOpsError(e) : (e && e.message) || describeOpsError(e));

  const load = async ({ includeClosed } = {}) => {
    setLoading(true);
    setError(null);
    setDetails({});
    try {
      const tokenResult = await readOpsToken(orgId);
      if (tokenResult.error) {
        setError(tokenResult.error);
        setOpenTickets([]);
        return;
      }
      setToken(tokenResult.token);
      const [open, closed] = await Promise.all([
        fetchSide(tokenResult.token, 'open'),
        includeClosed ? fetchSide(tokenResult.token, 'closed') : Promise.resolve(null),
      ]);
      setOpenTickets(open);
      if (includeClosed) setClosedTickets(closed);
      setLoadedAt(new Date());
    } catch (e) {
      setError(errorText(e));
      setOpenTickets([]);
      if (includeClosed) setClosedTickets(null);
    } finally {
      setLoading(false);
    }
  };

  // Closed tickets are fetched only when the user asks for them (All / Closed).
  const loadClosed = async () => {
    if (closedTickets || closedLoading || !token) return;
    setClosedLoading(true);
    try {
      setClosedTickets(await fetchSide(token, 'closed'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setClosedLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Load once per organisation; Refresh reloads on demand.
  }, [orgId]);

  useEffect(() => {
    if (state === 'ALL' || state === 'CLOSED') loadClosed();
  }, [state, token]);

  const openTicket = async (ticketId) => {
    setOpenTicketId(ticketId);
    if (details[ticketId] && !details[ticketId].error) return;
    setDetails(prev => ({ ...prev, [ticketId]: { loading: true } }));
    try {
      const response = await getOpsTicket(token, ticketId);
      setDetails(prev => ({ ...prev, [ticketId]: { data: response.data } }));
    } catch (e) {
      setDetails(prev => ({ ...prev, [ticketId]: { error: describeOpsError(e) } }));
    }
  };

  const tickets = useMemo(() => (closedTickets ? [...openTickets, ...closedTickets] : openTickets), [openTickets, closedTickets]);

  const options = useMemo(() => ({
    statuses: distinct(tickets, t => t.status && t.status.name),
    assignees: distinct(tickets, t => t.assignedTo && t.assignedTo.name),
    locations: distinct(tickets, t => t.location),
  }), [tickets]);

  const summary = useMemo(() => tickets.reduce((acc, t) => {
    acc.total += 1;
    acc[stateOf(t)] += 1;
    if (t.isEscalated && stateOf(t) === 'OPEN') acc.escalated += 1;
    if (isOverdue(t)) acc.overdue += 1;
    return acc;
  }, { total: 0, OPEN: 0, CLOSED: 0, UNKNOWN: 0, escalated: 0, overdue: 0 }), [tickets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const [from, to] = dateRange || [];
    return tickets.filter(t => {
      if (state !== 'ALL' && stateOf(t) !== state) return false;
      if (statuses.length && !(t.status && statuses.includes(t.status.name))) return false;
      if (source && t.source !== source) return false;
      if (assignees.length && !(t.assignedTo && assignees.includes(t.assignedTo.name))) return false;
      if (locations.length && !locations.includes(t.location)) return false;
      if (escalatedOnly && !(t.isEscalated && stateOf(t) === 'OPEN')) return false;
      if (overdueOnly && !isOverdue(t)) return false;
      if (from && to) {
        // A ticket with no complaint date cannot be placed in a range, so it is excluded while a range is set.
        if (!t.complaintDate) return false;
        const d = moment(t.complaintDate);
        if (d.isBefore(from.clone().startOf('day')) || d.isAfter(to.clone().endOf('day'))) return false;
      }
      if (q) {
        const hay = [t.ticketNo, t.assetCode, t.ownerCode, t.detailsOfComplaint, t.assetproJobCardNo, t.assetproComplaintNo]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, state, search, statuses, source, assignees, locations, dateRange, escalatedOnly, overdueOnly]);

  const resetFilters = () => {
    setState('OPEN'); setSearch(''); setStatuses([]); setSource(undefined); setAssignees([]);
    setLocations([]); setDateRange([]); setEscalatedOnly(false); setOverdueOnly(false);
  };

  const columns = [
    { title: 'Ticket No', dataIndex: 'ticketNo', key: 'ticketNo', width: 140, sorter: strSorter('ticketNo'), render: v => <a>{v || EMPTY}</a> },
    { title: 'Asset Code', dataIndex: 'assetCode', key: 'assetCode', width: 110, sorter: strSorter('assetCode'), render: v => v || EMPTY },
    {
      title: 'Details', dataIndex: 'detailsOfComplaint', key: 'detailsOfComplaint', width: 260,
      render: v => <div style={{ maxWidth: 260, whiteSpace: 'normal' }}>{v || EMPTY}</div>,
    },
    {
      title: 'Status', key: 'status', width: 190,
      sorter: (a, b) => String((a.status && a.status.name) || '').localeCompare(String((b.status && b.status.name) || '')),
      render: (_, t) => (
        <span>
          <StatusTag status={t.status} />
          {t.isEscalated && <Tooltip title={t.escalationReason}><span style={{ color: '#fa541c', fontSize: 12 }}>Escalated</span></Tooltip>}
          {isOverdue(t) && <div style={{ color: '#cf1322', fontSize: 12 }}>Overdue</div>}
        </span>
      ),
    },
    { title: 'Raised By', dataIndex: 'source', key: 'source', width: 150, render: v => (v ? SOURCE_LABELS[v] || v : EMPTY) },
    { title: 'Assigned To', key: 'assignedTo', width: 140, render: (_, t) => (t.assignedTo && t.assignedTo.name) || EMPTY },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 120, render: v => v || EMPTY },
    {
      title: 'Complaint Date', dataIndex: 'complaintDate', key: 'complaintDate', width: 150,
      sorter: (a, b) => moment(a.complaintDate || 0).valueOf() - moment(b.complaintDate || 0).valueOf(),
      render: v => formatDate(v, true),
    },
    {
      title: 'Target Date', dataIndex: 'targetDate', key: 'targetDate', width: 120,
      sorter: (a, b) => moment(a.targetDate || 0).valueOf() - moment(b.targetDate || 0).valueOf(),
      render: v => formatDate(v),
    },
    {
      title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', width: 150, defaultSortOrder: 'descend',
      sorter: (a, b) => moment(a.updatedAt || 0).valueOf() - moment(b.updatedAt || 0).valueOf(),
      render: v => formatDate(v, true),
    },
    { title: 'Closed At', dataIndex: 'closedAt', key: 'closedAt', width: 150, render: v => formatDate(v, true) },
  ];

  const stamp = moment().format('YYYYMMDD-HHmm');

  return (
    <Card
      className="gx-card"
      title="IoT Tickets Report"
      extra={(
        <span>
          {loadedAt && <span style={{ color: '#8c8c8c', fontSize: 12, marginRight: 12 }}>Loaded {moment(loadedAt).format('DD MMM YYYY HH:mm')}</span>}
          <Button icon="reload" onClick={() => load({ includeClosed: !!closedTickets })} loading={loading}>Refresh</Button>
        </span>
      )}
    >
      <div style={{ color: '#8c8c8c', marginBottom: 12 }}>
        All IoT tickets for your organisation from the Ops module — raised by you or by the AssetPro team — with their current status.
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <SummaryTile label="Open" value={summary.OPEN} color="#d46b08" active={state === 'OPEN'} onClick={() => setState('OPEN')} hint="Any status except Closed / Cancelled" />
        <SummaryTile label="Closed" value={closedTickets ? summary.CLOSED : closedLoading ? '…' : '—'} color="#389e0d" active={state === 'CLOSED'} onClick={() => setState('CLOSED')}
          hint={closedTickets ? 'Closed or Cancelled' : 'Closed tickets are not loaded yet — click to load them'} />
        <SummaryTile label="Total" value={closedTickets ? summary.total : closedLoading ? '…' : '—'} color="#262626" active={state === 'ALL'} onClick={() => setState('ALL')}
          hint={closedTickets ? 'Open and closed tickets' : 'Click to load closed tickets and show all'} />
        <SummaryTile label="Overdue" value={summary.overdue} color="#cf1322" active={overdueOnly} onClick={() => setOverdueOnly(v => !v)} hint="Open and past the target date (tickets without a target date are not counted)" />
        <SummaryTile label="Escalated" value={summary.escalated} color="#fa541c" active={escalatedOnly} onClick={() => setEscalatedOnly(v => !v)} hint="Open tickets currently escalated to the technical team" />
        {summary.UNKNOWN > 0 && (
          <SummaryTile label="Status unknown" value={summary.UNKNOWN} color="#ad8b00" active={state === 'UNKNOWN'} onClick={() => setState('UNKNOWN')} hint="No status category in Ops — counted as neither open nor closed" />
        )}
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={8} lg={6}>
          <Input.Search allowClear placeholder="Ticket no, asset code, details, job card…" value={search} onChange={e => setSearch(e.target.value)} />
        </Col>
        <Col xs={24} md={8} lg={5}>
          <Radio.Group value={state} onChange={e => setState(e.target.value)} buttonStyle="solid">
            <Radio.Button value="OPEN">Open</Radio.Button>
            <Radio.Button value="CLOSED">Closed</Radio.Button>
            <Radio.Button value="ALL">All</Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <RangePicker style={{ width: '100%' }} value={dateRange} onChange={v => setDateRange(v || [])} placeholder={['Complaint from', 'Complaint to']} format="DD MMM YYYY" />
        </Col>
        <Col xs={24} md={8} lg={7}>
          <Select mode="multiple" allowClear style={{ width: '100%' }} placeholder="Status" value={statuses} onChange={setStatuses}>
            {options.statuses.map(v => <Option key={v} value={v}>{v}</Option>)}
          </Select>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Select allowClear style={{ width: '100%' }} placeholder="Raised by" value={source} onChange={setSource}>
            {Object.keys(SOURCE_LABELS).map(k => <Option key={k} value={k}>{SOURCE_LABELS[k]}</Option>)}
          </Select>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Select mode="multiple" allowClear style={{ width: '100%' }} placeholder="Assigned to" value={assignees} onChange={setAssignees}>
            {options.assignees.map(v => <Option key={v} value={v}>{v}</Option>)}
          </Select>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Select mode="multiple" allowClear style={{ width: '100%' }} placeholder="Location" value={locations} onChange={setLocations}>
            {options.locations.map(v => <Option key={v} value={v}>{v}</Option>)}
          </Select>
        </Col>
        <Col xs={24} md={8} lg={6} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Checkbox checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)}>Overdue</Checkbox>
          <Checkbox checked={escalatedOnly} onChange={e => setEscalatedOnly(e.target.checked)}>Escalated</Checkbox>
          <a onClick={resetFilters}>Reset</a>
        </Col>
      </Row>

      <div style={{ color: '#8c8c8c', fontSize: 12 }}>
        Showing {filtered.length} of {tickets.length} {closedTickets ? '' : 'open '}ticket(s)
        {closedTickets ? '' : ' (closed tickets load when you choose Closed or All)'}. Click a ticket to see its details, attachments and timeline.
      </div>
      <ExportableTable
        columns={columns}
        dataSource={filtered}
        rowKey="id"
        size="small"
        loading={loading || (closedLoading && state !== 'OPEN')}
        scroll={{ x: 1700 }}
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
        exportName={`iot-tickets-${stamp}.csv`}
        exportHeader={CSV_HEADER}
        exportRow={csvRow}
        onRow={record => ({ onClick: () => openTicket(record.id), style: { cursor: 'pointer' } })}
      />

      <Modal
        visible={!!openTicketId}
        title="Ticket"
        width="90%"
        footer={null}
        onCancel={() => setOpenTicketId(null)}
      >
        {openTicketId && (
          <OpsTicketDetail key={openTicketId} detail={details[openTicketId]} token={token} onBack={() => setOpenTicketId(null)} />
        )}
      </Modal>
    </Card>
  );
}

const mapStateToProps = ({ auth }) => ({ authUser: auth.authUser });

export default connect(mapStateToProps)(OpsTicketsReport);
